import React from 'react';

interface MarkPaidModalProps {
  isOpen: boolean;
  propertyTitle: string;
  tenantName: string;
  paymentAmount: string;
  setPaymentAmount: (v: string) => void;
  paymentMethod: 'cash' | 'bank-transfer' | 'other';
  setPaymentMethod: (v: 'cash' | 'bank-transfer' | 'other') => void;
  paymentNotes: string;
  setPaymentNotes: (v: string) => void;
  arrears: number;
  currentBalance: number;
  totalToClear: number;
  onCancel: () => void;
  onConfirm: () => void;
  processingPayment: boolean;
  formatCurrency: (amount: number) => string;
}

const MarkPaidModal: React.FC<MarkPaidModalProps> = ({
  isOpen,
  propertyTitle,
  tenantName,
  paymentAmount,
  setPaymentAmount,
  paymentMethod,
  setPaymentMethod,
  paymentNotes,
  setPaymentNotes,
  arrears,
  currentBalance,
  totalToClear,
  onCancel,
  onConfirm,
  processingPayment,
  formatCurrency
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <h3 className="text-xl font-bold mb-4">Record Rent Payment</h3>

        <div className="mb-4">
          <p className="text-sm text-gray-600">Property</p>
          <p className="font-medium">{propertyTitle}</p>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600">Tenant</p>
          <p className="font-medium">{tenantName}</p>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Payment Amount (€)
          </label>
          <input
            type="number"
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="0.00"
            step="0.01"
            min="0"
          />
        </div>

        <div className="bg-gray-50 p-4 rounded mb-4">
          <div className="flex justify-between mb-2">
            <span>Arrears (Past Due):</span>
            <span className="font-bold text-red-600">
              {formatCurrency(arrears || 0)}
            </span>
          </div>
          <div className="flex justify-between mb-2">
            <span>Current Month Balance:</span>
            <span>{formatCurrency(currentBalance)}</span>
          </div>
          <div className="flex justify-between border-t pt-2 font-bold">
            <span>Total to Clear Account:</span>
            <span>{formatCurrency(totalToClear)}</span>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Payment Method
          </label>
          <select
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value as any)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="bank-transfer">Bank Transfer</option>
            <option value="cash">Cash</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notes (Optional)
          </label>
          <textarea
            value={paymentNotes}
            onChange={(e) => setPaymentNotes(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            rows={3}
            placeholder="Add any notes about this payment..."
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={processingPayment}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            disabled={processingPayment}
          >
            {processingPayment ? 'Processing...' : 'Record Payment'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MarkPaidModal;
